import { useEffect, useState, useMemo } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/sections/Footer';
import { ScrollReveal } from '@/components/effects/ScrollReveal';
import ServiceCalloutBanner from '@/components/shared/ServiceCalloutBanner';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  ArrowDown,
  Eye,
  Star,
  Target,
  Search,
  MapPin,
  Wrench,
  FileText,
  MousePointerClick,
  Megaphone,
  Briefcase,
  Repeat,
  Mail,
  PenLine,
  Send,
  Newspaper,
  Instagram,
  MessageSquare,
  BarChart3,
  LineChart,
  Percent,
  Users,
} from 'lucide-react';
import { useSEO, SITE_URL } from '@/hooks/useSEO';

const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

const CARD_STYLE = {
  background: 'var(--l1)',
  border: '1px solid var(--b1)',
  boxShadow: '0 1px 0 rgba(255,255,255,0.02) inset',
};

const GAP = [
  { icon: Eye,    title: 'Visibility',  body: 'If your ideal customer cannot find you on Google, on LinkedIn, or through a referral — they will find your competitor instead. Visibility is not optional. It is revenue.' },
  { icon: Star,   title: 'Credibility', body: 'Being found is only the first step. Your digital presence needs to make the case that you are the obvious choice before a prospect ever speaks to you.' },
  { icon: Target, title: 'Conversion',  body: 'Traffic without conversion is just noise. Every element of your digital presence needs to be working together to turn visitors into enquiries and enquiries into clients.' },
];

const CATEGORIES = [
  {
    title: 'Search and Visibility',
    items: [
      { icon: Search,   title: 'SEO Strategy and Implementation',   body: 'Rank for the keywords your ideal customers are actually searching. Long-term organic growth that compounds month on month.' },
      { icon: MapPin,   title: 'Local SEO and Google Business Profile', body: 'Dominate local search results and Google Maps. Essential for any business with a physical presence or local client base.' },
      { icon: Wrench,   title: 'Technical SEO Audit and Fixes',     body: 'Speed, structure, and crawlability. The foundation that everything else depends on.' },
      { icon: FileText, title: 'Content Strategy and Creation',     body: 'Content that answers real questions, ranks on Google, and positions you as the authority in your space.' },
    ],
  },
  {
    title: 'Paid Media',
    items: [
      { icon: MousePointerClick, title: 'Google Ads Management',          body: 'Appear at the top of Google the moment someone searches for what you offer. Only pay when they click.' },
      { icon: Megaphone,         title: 'Meta Ads — Facebook and Instagram', body: 'Reach your ideal customers where they spend their time. Precise targeting. Measurable results.' },
      { icon: Briefcase,         title: 'LinkedIn Ads',                    body: 'The most powerful B2B advertising platform available. Reach decision makers by job title, company, and industry.' },
      { icon: Repeat,            title: 'Retargeting Campaigns',           body: 'Re-engage visitors who showed interest but did not convert. One of the highest ROI channels available.' },
    ],
  },
  {
    title: 'Email Marketing',
    items: [
      { icon: Mail,    title: 'Email List Building Strategy',    body: 'Build a direct line to your customers that no algorithm can take away.' },
      { icon: PenLine, title: 'Campaign Design and Copywriting', body: 'Emails that get opened, read, and acted on. Not ignored.' },
      { icon: Send,    title: 'Automated Sequences',             body: 'Welcome sequences, nurture campaigns, re-engagement flows. Working for you around the clock.' },
    ],
  },
  {
    title: 'Content and Brand',
    items: [
      { icon: Newspaper,     title: 'Blog Content Strategy',            body: 'Consistent content that builds authority, drives organic traffic, and answers the questions your customers are asking.' },
      { icon: Instagram,     title: 'Social Media Content Calendar',    body: 'A consistent, on-brand presence across the platforms your customers use.' },
      { icon: MessageSquare, title: 'Brand Messaging and Positioning',  body: 'Clarity on who you are, who you serve, and why you are the right choice. The foundation everything else is built on.' },
    ],
  },
  {
    title: 'Analytics and Reporting',
    items: [
      { icon: BarChart3, title: 'Google Analytics 4 Setup and Configuration', body: 'Know exactly what is working and what is not. Data-driven decisions start here.' },
      { icon: LineChart, title: 'Monthly Performance Reporting',              body: 'Clear, actionable reports that show progress against the outcomes that matter to your business.' },
      { icon: Percent,   title: 'Conversion Rate Optimisation',               body: 'More revenue from the traffic you already have. Small improvements compound into significant results.' },
      { icon: Users,     title: 'Competitor Analysis',                        body: 'Know exactly what your competitors are doing digitally — and where you can beat them.' },
    ],
  },
];

const PACKAGES = [
  {
    id: 'reach-visibility-starter',
    label: 'Acodia Reach — Visibility Starter',
    title: 'Visibility Starter',
    body: 'SEO, Google Business Profile, and monthly performance reporting. The foundation for businesses that want to get found.',
  },
  {
    id: 'reach-growth-accelerator',
    label: 'Acodia Reach — Growth Accelerator',
    title: 'Growth Accelerator',
    body: 'SEO, paid media, email automation, and full reporting. For businesses ready to invest in serious growth.',
  },
  {
    id: 'reach-full-reach-retainer',
    label: 'Acodia Reach — Full Reach Retainer',
    title: 'Full Reach Retainer',
    body: 'Every channel, fully managed. For businesses that want to own their market.',
  },
  {
    id: 'reach-one-off-growth-audit',
    label: 'Acodia Reach — One-off Growth Audit',
    title: 'One-off Growth Audit',
    body: 'A comprehensive audit of your entire digital presence with a clear prioritised roadmap. The starting point before any investment.',
  },
];

function scrollTo(id) {
  return (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
}

function formatPrice(row) {
  if (!row || !row.price || Number(row.price) === 0) return 'Price on consultation';
  const gbp = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(Number(row.price));
  return row.per_month ? `${gbp}/month` : gbp;
}

export default function ReachPage() {
  useSEO({
    title: 'Reach | Acodia — Grow. Defend. Be Found.',
    description: 'SEO, paid media, email, and content that converts. Build the digital presence your business deserves. Free growth audit for new clients.',
    path: '/reach',
    ogImage: `${SITE_URL}/images/og/og-reach.png`,
  });

  const [pricingRows, setPricingRows] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/service-pricing`);
        if (!res.ok) return; // Endpoint not yet live — packages fall back to "Price on consultation"
        const body = await res.json();
        if (!cancelled) setPricingRows(body.rows || []);
      } catch {
        // Silent fall-through — fallback strings are fine
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const priceById = useMemo(() => {
    const map = {};
    for (const r of pricingRows) map[r.id] = r;
    return map;
  }, [pricingRows]);

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh', color: 'var(--white)' }} data-testid="reach-page">
      <Navbar />

      {/* ── HERO ── */}
      <section
        data-testid="reach-hero"
        className="relative pt-32 pb-20 sm:pt-40 sm:pb-28"
        style={{ background: 'var(--bg)' }}
      >
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'var(--hero-mesh)' }} />
        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <p className="font-body text-xs uppercase tracking-[0.22em] mb-3" style={{ color: 'var(--a)' }}>
              Acodia Reach
            </p>
            <h1
              className="font-display font-extrabold tracking-[-0.03em] mb-5 max-w-[22ch]"
              style={{ fontSize: 'clamp(2.25rem, 5vw, 4rem)', color: 'var(--white)' }}
            >
              Your Competitors Are Getting Found. You Should Be Too.
            </h1>
            <p className="font-body font-light text-base sm:text-lg max-w-3xl mb-8" style={{ color: 'var(--tm)', lineHeight: '1.72' }}>
              Being good at what you do is not enough if nobody can find you. Acodia Reach builds the digital presence
              your business deserves — through SEO, paid media, email, and content that actually converts.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="/pricing?service=reach"
                data-testid="reach-hero-primary"
                className="btn-shimmer inline-flex items-center font-body text-sm h-11 px-6 rounded-[3px]"
                style={{ background: 'var(--a)', color: 'var(--white)' }}
              >
                Get a Free Growth Audit <ArrowRight size={14} className="ml-2" />
              </a>
              <Button
                data-testid="reach-hero-secondary"
                onClick={scrollTo('reach-services')}
                className="font-body text-sm h-11 px-6 rounded-[3px]"
                style={{ background: 'transparent', color: 'var(--tm)', border: '1px solid var(--b1)' }}
              >
                See Our Services <ArrowDown size={14} className="ml-2" />
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── THE GAP ── */}
      <section data-testid="reach-gap" className="py-14 sm:py-20" style={{ background: 'var(--l2)' }}>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2
              className="font-display font-extrabold tracking-[-0.03em] mb-10 max-w-[28ch]"
              style={{ fontSize: 'clamp(2rem, 4.5vw, 3.2rem)', color: 'var(--white)' }}
            >
              The Difference Between Businesses That Grow and Businesses That Plateau.
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {GAP.map(({ icon: Icon, title, body }, i) => (
              <ScrollReveal key={title} delay={i * 0.08}>
                <div className="p-6 rounded-lg h-full" style={CARD_STYLE}>
                  <div
                    className="flex items-center justify-center rounded mb-4"
                    style={{
                      width: 40,
                      height: 40,
                      background: 'var(--a-glow)',
                      border: '1px solid var(--a-border)',
                    }}
                  >
                    <Icon size={18} style={{ color: 'var(--a)' }} />
                  </div>
                  <h3 className="font-display text-lg font-semibold mb-2" style={{ color: 'var(--white)' }}>
                    {title}
                  </h3>
                  <p className="font-body text-sm" style={{ color: 'var(--tm)', lineHeight: '1.65' }}>
                    {body}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES GRID (5 categories) ── */}
      <section data-testid="reach-services" id="reach-services" className="py-14 sm:py-20" style={{ background: 'var(--bg)' }}>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="mb-12 max-w-3xl">
              <h2
                className="font-display font-extrabold tracking-[-0.03em] mb-4"
                style={{ fontSize: 'clamp(2rem, 4.5vw, 3.2rem)', color: 'var(--white)' }}
              >
                Every Channel. One Strategy.
              </h2>
              <p className="font-body font-light text-base" style={{ color: 'var(--tm)', lineHeight: '1.72' }}>
                We do not offer isolated services. We build integrated digital strategies where every channel works
                together toward the same outcome.
              </p>
            </div>
          </ScrollReveal>

          {CATEGORIES.map((cat, idx) => (
            <div key={cat.title} className={idx === 0 ? '' : 'mt-14'}>
              <ScrollReveal>
                <div className="flex items-center gap-3 mb-5">
                  <span
                    className="font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded"
                    style={{ background: 'var(--a-glow)', color: 'var(--a)', border: '1px solid var(--a-border)' }}
                  >
                    category · 0{idx + 1}
                  </span>
                  <h3 className="font-display text-xl font-semibold" style={{ color: 'var(--white)' }}>
                    {cat.title}
                  </h3>
                </div>
              </ScrollReveal>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {cat.items.map(({ icon: Icon, title, body }, i) => (
                  <ScrollReveal key={title} delay={i * 0.04}>
                    <div className="p-5 rounded-lg h-full" style={CARD_STYLE}>
                      <div
                        className="flex items-center justify-center rounded mb-3"
                        style={{
                          width: 32,
                          height: 32,
                          background: 'var(--a-glow)',
                          border: '1px solid var(--a-border)',
                        }}
                      >
                        <Icon size={14} style={{ color: 'var(--a)' }} />
                      </div>
                      <h4 className="font-display text-sm font-semibold mb-1.5" style={{ color: 'var(--white)' }}>
                        {title}
                      </h4>
                      <p className="font-body text-xs" style={{ color: 'var(--tm)', lineHeight: '1.65' }}>
                        {body}
                      </p>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-8 sm:py-10" style={{ background: 'var(--bg)' }}>
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
          <ServiceCalloutBanner
            testId="reach-callout-banner"
            text="Not sure where your biggest opportunity is? A free growth audit will show you exactly what to focus on first."
            ctaText="Book your free growth audit →"
            ctaUrl="/pricing?service=reach"
          />
        </div>
      </section>

      {/* ── PACKAGES ── */}
      <section data-testid="reach-packages" className="py-14 sm:py-20" style={{ background: 'var(--l2)' }}>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2
              className="font-display font-extrabold tracking-[-0.03em] mb-10 max-w-[24ch]"
              style={{ fontSize: 'clamp(2rem, 4.5vw, 3.2rem)', color: 'var(--white)' }}
            >
              Start with What You Need. Scale as You Grow.
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {PACKAGES.map((pkg, i) => {
              const priceRow = priceById[pkg.id];
              const price = formatPrice(priceRow);
              return (
                <ScrollReveal key={pkg.id} delay={i * 0.06}>
                  <div
                    data-testid={`reach-package-${pkg.id}`}
                    className="p-6 rounded-lg h-full flex flex-col"
                    style={CARD_STYLE}
                  >
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: 'var(--a)' }}>
                      {pkg.label}
                    </p>
                    <h3 className="font-display text-lg font-semibold mb-2" style={{ color: 'var(--white)' }}>
                      {pkg.title}
                    </h3>
                    <p className="font-body text-sm mb-4 flex-1" style={{ color: 'var(--tm)', lineHeight: '1.65' }}>
                      {pkg.body}
                    </p>
                    <p className="font-mono text-sm mb-4" style={{ color: 'var(--white)' }}>
                      Starting from <span style={{ color: 'var(--a)' }}>{price}</span>
                    </p>
                    <a
                      href="/pricing?service=reach"
                      className="btn-shimmer inline-flex items-center justify-center font-body text-xs h-10 rounded-[3px]"
                      style={{ background: 'transparent', color: 'var(--a)', border: '1px solid var(--a-border)' }}
                    >
                      Explore <ArrowRight size={12} className="ml-1" />
                    </a>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section data-testid="reach-cta" className="py-16 sm:py-24" style={{ background: 'var(--bg)' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h2
              className="font-display font-extrabold tracking-[-0.03em] mb-4"
              style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)', color: 'var(--white)' }}
            >
              Not Sure Where Your Biggest Opportunity Is?
            </h2>
            <p className="font-body font-light text-base mb-8" style={{ color: 'var(--tm)', lineHeight: '1.72' }}>
              A free growth audit will show you exactly where you are leaving revenue on the table and what to do about
              it first.
            </p>
            <a
              href="/pricing?service=reach"
              data-testid="reach-cta-button"
              className="btn-shimmer inline-flex items-center font-body text-sm h-12 px-7 rounded-[3px]"
              style={{ background: 'var(--a)', color: 'var(--white)' }}
            >
              Book My Free Growth Audit <ArrowRight size={14} className="ml-2" />
            </a>
          </ScrollReveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
